/**
 * *Add kits…*'s Contents and Drops tables (V1 completion R4; the agreed mock-up's dialog): the command shell's
 * custom widget for `orders.add_kits_to_order`. The shell keeps everything else (where the kits are packed, the
 * supplier and prices, the delivery choice, the dry run's notes and the tick to create addresses); this draws what one
 * kit holds and the drop list, driven by the dry run.
 *
 * - **Contents**: a row for each line of the order a kit could hold (the dry run starts them all), each with the
 *   quantity per kit; stock items are added by their reference. Where a line has several versions, each drop names
 *   which it takes.
 * - **Drops**: pasted, uploaded or copied from a previous order; the dry run reads the list into rows (this widget
 *   adopts them), says which addresses are the customer's and which will be created, and folds identical drops into
 *   kit lines. The table draws the first 50 rows; the list can be 2,000.
 *
 * Orders decides every rule; the field errors it returns land beside the rows they name.
 */
import { useEffect, useRef, useState } from "react";
import {
  contentFallbackLabel,
  contentHints,
  dropLabel,
  dropPlaces,
  dropsCopiedFrom,
  dropTotals,
  dropVersion,
  wholeNumberOrNull,
  withDropVersion,
  withoutContent,
  type ContentHint,
  type DropPlaceState,
  type KitContentBody,
  type KitDropBody,
} from "@shared-core/domain/add-kits";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import { Textarea } from "../../primitives/textarea";
import type {
  CustomWidget,
  CustomWidgetProps,
} from "../../patterns/command-shell/types";

/** The body paths the widget owns. */
export const ADD_KITS_PATHS = [
  "contents",
  "drops",
  "drops_csv",
  "copy_drops_from_order_no",
] as const;

/** Drops drawn at once; the rest are counted. */
export const DROP_ROWS_SHOWN = 50;

const arrayOf = <T,>(value: unknown): T[] =>
  Array.isArray(value) ? (value as T[]) : [];

const PLACE_PILL: Record<
  DropPlaceState,
  { label: string; variant: "secondary" | "outline" | "warning" }
> = {
  matched: { label: "matched", variant: "secondary" },
  to_create: { label: "to create", variant: "outline" },
  unresolved: { label: "not found", variant: "warning" },
};

function AddKits({
  body,
  setValues,
  errors,
  hints,
  dryRun,
  disabled,
}: CustomWidgetProps) {
  const contents = arrayOf<KitContentBody>(body.contents);
  const drops = arrayOf<KitDropBody>(body.drops);
  const rows = contentHints(hints);
  const places = dropPlaces(hints);
  const copiedFrom = dropsCopiedFrom(hints);
  const csv = typeof body.drops_csv === "string" ? body.drops_csv : "";
  const copyFrom =
    typeof body.copy_drops_from_order_no === "string"
      ? body.copy_drops_from_order_no
      : "";
  const isReading = drops.length === 0 && (csv !== "" || copyFrom !== "");

  // The dry run reads a pasted, uploaded or copied list into rows: adopt them (and a copied order's contents), and let
  // go of a pasted text, so the person edits rows from then on. A copy keeps naming its order, so the dry run can say
  // how far the rows have since been edited.
  const read = dryRun?.prefill as {
    drops?: KitDropBody[];
    contents?: KitContentBody[];
  } | null;
  const adopted = useRef<unknown>(null);
  useEffect(() => {
    if (!isReading || !read?.drops?.length || adopted.current === read) return;
    adopted.current = read;
    setValues({
      drops: read.drops,
      drops_csv: undefined,
      ...(contents.length === 0 && read.contents
        ? { contents: read.contents }
        : {}),
    });
  }, [isReading, read, contents.length, setValues]);

  const errorsAt = (prefix: string) =>
    errors.filter(
      (e) => e.field === prefix || e.field.startsWith(`${prefix}.`),
    );

  return (
    <div className="space-y-5">
      <ContentsTable
        contents={contents}
        hints={rows}
        errors={errorsAt("contents")}
        disabled={disabled}
        onChange={(next) => setValues({ contents: next }, { debounce: true })}
        onRemove={(index) => {
          const next = withoutContent(contents, drops, index);
          setValues({ contents: next.contents, drops: next.drops });
        }}
      />
      <DropsTable
        drops={drops}
        contents={contents}
        hints={rows}
        places={places}
        copiedFrom={copiedFrom}
        errors={[
          ...errorsAt("drops"),
          ...errorsAt("drops_csv"),
          ...errorsAt("copy_drops_from_order_no"),
        ]}
        isReading={isReading}
        disabled={disabled}
        onChange={(next) =>
          setValues(
            {
              drops: next,
              // An emptied list is not read back from the order it was copied from.
              ...(next.length === 0
                ? { copy_drops_from_order_no: undefined }
                : {}),
            },
            { debounce: true },
          )
        }
        onRead={(text) =>
          setValues({
            drops_csv: text,
            copy_drops_from_order_no: undefined,
            drops: [],
          })
        }
        onCopy={(orderNo) =>
          // Contents left as the dry run started them are replaced by the previous order's, mapped onto this order's.
          setValues({
            copy_drops_from_order_no: orderNo,
            drops_csv: undefined,
            drops: [],
            ...(contents.every((c) => c.per_kit == null)
              ? { contents: [] }
              : {}),
          })
        }
        onClear={() =>
          setValues({ drops: [], copy_drops_from_order_no: undefined })
        }
      />
    </div>
  );
}

// ── Contents ──────────────────────────────────────────────────────────────

function ContentsTable({
  contents,
  hints,
  errors,
  disabled,
  onChange,
  onRemove,
}: {
  contents: KitContentBody[];
  hints: readonly ContentHint[];
  errors: Array<{ field: string; message: string }>;
  disabled: boolean;
  onChange: (next: KitContentBody[]) => void;
  onRemove: (index: number) => void;
}) {
  const [stockRef, setStockRef] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const setPerKit = (index: number, text: string) => {
    if (text.trim() !== "" && wholeNumberOrNull(text) === null) return;
    onChange(
      contents.map((c, i) =>
        i === index ? { ...c, per_kit: wholeNumberOrNull(text) } : c,
      ),
    );
  };
  const addStock = () => {
    const ref = stockRef.trim();
    if (!ref) return;
    onChange([...contents, { stock_item_ref: ref, per_kit: 1 }]);
    setStockRef("");
    setIsAdding(false);
  };
  const errorAt = (index: number) =>
    errors.some((e) => e.field.startsWith(`contents.${index}.`));
  return (
    <section aria-labelledby="add-kits-contents" className="space-y-2">
      <h3 id="add-kits-contents" className="text-sm font-semibold">
        Contents{" "}
        <span className="font-normal text-muted-foreground">
          (what one kit holds)
        </span>
      </h3>
      {contents.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th scope="col" className="py-1 pr-3 font-normal">
                Content
              </th>
              <th scope="col" className="py-1 pr-3 font-normal">
                From
              </th>
              <th scope="col" className="py-1 pr-3 text-right font-normal">
                Per kit
              </th>
              <th scope="col" className="py-1">
                <span className="sr-only">Remove</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {contents.map((content, i) => {
              const hint = hints[i];
              const label = hint?.label || contentFallbackLabel(content);
              return (
                <tr
                  key={`${content.line_ref ?? content.stock_item_ref}-${i}`}
                  className="border-t border-border align-top"
                >
                  <td className="py-1.5 pr-3">
                    {label}
                    {hint?.versions && (
                      <span className="block text-xs text-muted-foreground">
                        version per drop
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 pr-3 text-muted-foreground">
                    {hint?.from_label ?? ""}
                  </td>
                  <td className="py-1.5 pr-3 text-right">
                    <Input
                      type="text"
                      inputMode="numeric"
                      className="ml-auto w-20 text-right tabular-nums"
                      aria-label={`Per kit: ${label}`}
                      aria-invalid={errorAt(i) || undefined}
                      disabled={disabled}
                      value={content.per_kit ?? ""}
                      onChange={(e) => setPerKit(i, e.target.value)}
                    />
                  </td>
                  <td className="py-1.5 text-right">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={disabled}
                      aria-label={`Take ${label} out of the kit`}
                      onClick={() => onRemove(i)}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {errors.map((e) => (
        <p
          key={`${e.field}-${e.message}`}
          role="alert"
          className="text-sm text-red-600"
        >
          {e.message}
        </p>
      ))}
      {contents.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Nothing in the kit yet: add a stock item, or a line of this order.
        </p>
      )}
      {isAdding ? (
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label htmlFor="add-kits-stock-ref">Stock item</Label>
            <Input
              id="add-kits-stock-ref"
              className="w-56"
              placeholder="Stock item reference"
              value={stockRef}
              onChange={(e) => setStockRef(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addStock();
                }
              }}
            />
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={disabled || stockRef.trim() === ""}
            onClick={addStock}
          >
            Add to the kit
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setIsAdding(false)}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={() => setIsAdding(true)}
        >
          Add a content…
        </Button>
      )}
    </section>
  );
}

// ── Drops ─────────────────────────────────────────────────────────────────

type Starting = "paste" | "copy" | null;

function DropsTable({
  drops,
  contents,
  hints,
  places,
  copiedFrom,
  errors,
  isReading,
  disabled,
  onChange,
  onRead,
  onCopy,
  onClear,
}: {
  drops: KitDropBody[];
  contents: KitContentBody[];
  hints: readonly ContentHint[];
  places: readonly DropPlaceState[];
  /** What the list says of the order it was copied from, worded by the dry run. */
  copiedFrom: string | null;
  errors: Array<{ field: string; message: string }>;
  isReading: boolean;
  disabled: boolean;
  onChange: (next: KitDropBody[]) => void;
  onRead: (text: string) => void;
  onCopy: (orderNo: string) => void;
  onClear: () => void;
}) {
  const [starting, setStarting] = useState<Starting>(null);
  const [pasted, setPasted] = useState("");
  const [previous, setPrevious] = useState("");
  const [fileProblem, setFileProblem] = useState<string | null>(null);
  const perDrop = hints.flatMap((hint, i) =>
    hint.versions ? [{ index: i, hint }] : [],
  );
  const totals = dropTotals(drops);
  const shown = drops.slice(0, DROP_ROWS_SHOWN);
  const errorAt = (index: number) =>
    errors.some((e) => e.field.startsWith(`drops.${index}.`));
  const listErrors = errors.filter((e) => !/^drops\.\d+\./.test(e.field));
  const rowErrors = errors.filter((e) => /^drops\.\d+\./.test(e.field));

  const upload = async (file: File | undefined) => {
    if (!file) return;
    try {
      onRead(await file.text());
      setFileProblem(null);
      setStarting(null);
    } catch {
      setFileProblem(
        "Couldn't read that file. Check it's a CSV and try again.",
      );
    }
  };
  const setKits = (index: number, text: string) => {
    if (text.trim() !== "" && wholeNumberOrNull(text) === null) return;
    onChange(
      drops.map((d, i) =>
        i === index ? { ...d, kits: wholeNumberOrNull(text) } : d,
      ),
    );
  };
  const setVersion = (index: number, content: number, text: string) =>
    onChange(
      drops.map((d, i) =>
        i === index ? withDropVersion(d, content, text) : d,
      ),
    );

  return (
    <section aria-labelledby="add-kits-drops" className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 id="add-kits-drops" className="text-sm font-semibold">
          Drops
          {drops.length > 0 && (
            <span className="font-normal text-muted-foreground">
              {" "}
              ({totals.drops.toLocaleString("en-GB")}{" "}
              {totals.drops === 1 ? "drop" : "drops"},{" "}
              {totals.kits.toLocaleString("en-GB")}{" "}
              {totals.kits === 1 ? "kit" : "kits"})
            </span>
          )}
          {drops.length > 0 && copiedFrom && (
            <span className="font-normal text-muted-foreground">
              {" "}
              · {copiedFrom}
            </span>
          )}
        </h3>
        {drops.length > 0 && (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto px-0"
            disabled={disabled}
            onClick={onClear}
          >
            Start the list again
          </Button>
        )}
      </div>

      {drops.length === 0 && (
        <div className="space-y-3 rounded-md border border-dashed border-border bg-muted/30 p-3">
          <p className="text-sm">
            Paste or upload the drop list, or copy last time&apos;s.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={disabled || isReading}
              onClick={() => setStarting(starting === "paste" ? null : "paste")}
            >
              Paste…
            </Button>
            <Label
              className="inline-flex h-8 cursor-pointer items-center rounded-md border border-border bg-background px-3 text-sm font-medium hover:bg-accent"
              htmlFor="add-kits-upload"
            >
              Upload CSV…
            </Label>
            <input
              id="add-kits-upload"
              type="file"
              accept=".csv,text/csv"
              className="sr-only"
              disabled={disabled || isReading}
              onChange={(e) => void upload(e.target.files?.[0])}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={disabled || isReading}
              onClick={() => setStarting(starting === "copy" ? null : "copy")}
            >
              Copy from a previous order…
            </Button>
          </div>
          {starting === "paste" && (
            <div className="space-y-2">
              <Label htmlFor="add-kits-paste">Paste the drop list</Label>
              <Textarea
                id="add-kits-paste"
                rows={5}
                placeholder="Address, postcode, kits…"
                value={pasted}
                onChange={(e) => setPasted(e.target.value)}
              />
              <Button
                type="button"
                size="sm"
                disabled={disabled || pasted.trim() === ""}
                onClick={() => {
                  onRead(pasted);
                  setStarting(null);
                }}
              >
                Read the list
              </Button>
            </div>
          )}
          {starting === "copy" && (
            <div className="flex flex-wrap items-end gap-2">
              <div className="space-y-1">
                <Label htmlFor="add-kits-copy">Previous order</Label>
                <Input
                  id="add-kits-copy"
                  className="w-48"
                  placeholder="e.g. ORD-K58816"
                  value={previous}
                  onChange={(e) => setPrevious(e.target.value)}
                />
              </div>
              <Button
                type="button"
                size="sm"
                disabled={disabled || previous.trim() === ""}
                onClick={() => {
                  onCopy(previous.trim());
                  setStarting(null);
                }}
              >
                Copy its drops
              </Button>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Columns: address reference or name, address and postcode; kits; a
            version for any content that differs by drop.
          </p>
          {isReading && (
            <p role="status" className="text-sm text-muted-foreground">
              Reading the list…
            </p>
          )}
          {fileProblem && (
            <p role="alert" className="text-sm text-red-600">
              {fileProblem}
            </p>
          )}
        </div>
      )}

      {drops.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th scope="col" className="py-1 pr-3 font-normal">
                  Address
                </th>
                <th scope="col" className="py-1 pr-3 font-normal">
                  Postcode
                </th>
                <th scope="col" className="py-1 pr-3 text-right font-normal">
                  Kits
                </th>
                {perDrop.map(({ index, hint }) => (
                  <th key={index} scope="col" className="py-1 pr-3 font-normal">
                    {hint.label} version
                  </th>
                ))}
                <th scope="col" className="py-1">
                  <span className="sr-only">Address</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((drop, i) => {
                const place = places[i];
                return (
                  <tr key={i} className="border-t border-border align-top">
                    <td className="py-1.5 pr-3">{dropLabel(drop, i)}</td>
                    <td className="py-1.5 pr-3 tabular-nums">
                      {drop.postcode ?? ""}
                    </td>
                    <td className="py-1.5 pr-3 text-right">
                      <Input
                        type="text"
                        inputMode="numeric"
                        className="ml-auto w-16 text-right tabular-nums"
                        aria-label={`Kits: ${dropLabel(drop, i)}`}
                        aria-invalid={errorAt(i) || undefined}
                        disabled={disabled}
                        value={drop.kits ?? ""}
                        onChange={(e) => setKits(i, e.target.value)}
                      />
                    </td>
                    {perDrop.map(({ index, hint }) => (
                      <td key={index} className="py-1.5 pr-3">
                        <Input
                          type="text"
                          className="w-24"
                          list={`add-kits-versions-${index}`}
                          aria-label={`${hint.label} version: ${dropLabel(drop, i)}`}
                          aria-invalid={errorAt(i) || undefined}
                          disabled={disabled}
                          value={dropVersion(drop, index) ?? ""}
                          onChange={(e) => setVersion(i, index, e.target.value)}
                        />
                      </td>
                    ))}
                    <td className="py-1.5">
                      {place && (
                        <Badge variant={PLACE_PILL[place].variant}>
                          {PLACE_PILL[place].label}
                        </Badge>
                      )}
                    </td>
                  </tr>
                );
              })}
              {drops.length > shown.length && (
                <tr className="border-t border-border">
                  <td
                    colSpan={4 + perDrop.length}
                    className="py-1.5 text-xs text-muted-foreground"
                  >
                    … {(drops.length - shown.length).toLocaleString("en-GB")}{" "}
                    more · {totals.drops.toLocaleString("en-GB")} drops,{" "}
                    {totals.kits.toLocaleString("en-GB")} kits
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {perDrop.map(({ index, hint }) => (
            <datalist key={index} id={`add-kits-versions-${index}`}>
              {hint.versions?.map((label) => (
                <option key={label} value={label} />
              ))}
            </datalist>
          ))}
        </div>
      )}

      {rowErrors.map((e) => (
        <p
          key={`${e.field}-${e.message}`}
          role="alert"
          className="text-sm text-red-600"
        >
          {e.message}
        </p>
      ))}
      {listErrors.map((e) => (
        <p
          key={`${e.field}-${e.message}`}
          role="alert"
          className="text-sm text-red-600"
        >
          {e.message}
        </p>
      ))}
      {contents.length === 0 && drops.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Add what one kit holds to see which versions each drop takes.
        </p>
      )}
    </section>
  );
}

export const addKitsWidget: CustomWidget = {
  ownedPaths: ADD_KITS_PATHS,
  Component: AddKits,
};
